/* eslint-disable */
import { Tooltip } from '@zoom/components';
import classnames from 'classnames';
import {
  addHours,
  addMinutes,
  eachDayOfInterval,
  getHours,
  getMinutes,
  isAfter,
  isBefore,
  startOfDay,
  startOfWeek,
} from 'date-fns';
import React, { useRef } from 'react';
import DatePickerComponent, { ReactDatePickerProps } from 'react-datepicker';
import { FieldRenderProps } from 'react-final-form';

import { useAuth } from '../../app/AuthorizationProvider';
import { handleEndOfDateRange } from '../../utils/dateRange';
import { DatePickerHeader } from './DatePickerHeader';
import { DatePickerTextFieldInput } from './DatePickerTextFieldInput';
import { WeekPickerTextFieldInput } from './WeekPickerTextFieldInput';
import { CustomHeaderParams } from './types';

export enum PickerType {
  day,
  week,
  month,
}

export interface DatePickerOptionalProps {
  dataTestId?: string;
  dateFormat?: string;
  disabled?: boolean;
  endDate?: Date;
  selectableWeekDays?: number[];
  label?: string;
  maxDate?: Date;
  maxDateRangeOverflow?: Date;
  minDate?: Date;
  minDateRangeOverflow?: Date;
  daysWithTooltip?: Date[];
  tooltipMessage?: React.ReactNode;
  selectsEnd?: boolean;
  selectsStart?: boolean;
  startDate?: Date;
  noValidation?: boolean;
  customOnChange?: (value: DatePickerValueType) => void;
  pickerType?: PickerType;
}

export type DatePickerValueType = Date | null;

interface DatePickerProps extends DatePickerOptionalProps, FieldRenderProps<DatePickerValueType> {
  inputId?: string;
  label?: string;
  maxDate?: Date;
  minDate?: Date;
}

/**
 * To support selecting value with keyboard, maxDate and minDate must be provided
 * as buggy logic in react-datepicker is not supporting a selection of minDate/maxDate with the keyboard
 * if there is no maxDate or minDate value set
 */
const maxDateDefaultValue = new Date('9999-01-01');
const minDateDefaultValue = new Date(0);

type CustomDayElementProps = {
  date: Date;
  dayOfMonth: number;
  daysWithTooltip: Date[];
  tooltipMessage?: React.ReactNode;
};

export const CustomDayElement: React.FC<CustomDayElementProps> = ({
  date,
  dayOfMonth,
  daysWithTooltip,
  tooltipMessage,
}) => {
  const content = <span>{dayOfMonth}</span>;

  if (
    daysWithTooltip.length &&
    tooltipMessage &&
    daysWithTooltip.filter((day) => day.getTime() === date.getTime()).length
  ) {
    return (
      <Tooltip placement="bottom" message={tooltipMessage as JSX.Element}>
        {content}
      </Tooltip>
    );
  }

  return content;
};

export const DatePicker: React.FC<DatePickerProps> = ({
  pickerType = PickerType.day,
  dataTestId,
  daysWithTooltip = [],
  disabled = false,
  dateFormat = 'yyyy-MM-dd',
  selectableWeekDays,
  endDate,
  inputId,
  label,
  maxDate: maxDateRounded = maxDateDefaultValue,
  maxDateRangeOverflow,
  meta,
  input: { value, onChange, ...restInput },
  minDate = minDateDefaultValue,
  minDateRangeOverflow,
  customOnChange = () => null,
  noValidation,
  startDate,
  selectsEnd,
  tooltipMessage,
  ...rest
}) => {
  const datePickerRef = useRef(null);

  const { localeObj } = useAuth();

  const inputRef = `ref-${inputId}`; // TODO: replace reference with useRef as soon as react-datepicker supports it.
  const isWeekPicker = pickerType === PickerType.week;

  const onChangeHandler = (selectedDate: DatePickerValueType) => {
    if (selectedDate) {
      let newSelectedDate = startOfDay(selectedDate);

      // Persist timeZone offset if value is preset/loaded with offset
      if (value && !isWeekOrMonthPicker) {
        newSelectedDate = addHours(newSelectedDate, getHours(value));
        newSelectedDate = addMinutes(newSelectedDate, getMinutes(value));
      }

      if (isWeekPicker) {
        newSelectedDate = startOfWeek(selectedDate, { locale: localeObj });
      }

      // To provide consistent selected date-picker value from click and keyboard actions
      onChange(newSelectedDate);
      customOnChange(newSelectedDate);
    } else {
      // Resetting input picker if `selectedDate` value is null
      onChange(null);
      customOnChange(null);
    }
  };

  const maxDate = maxDateRounded && selectsEnd ? handleEndOfDateRange(maxDateRounded) : maxDateRounded;

  const highlightDates = [] as Array<{ [key: string]: Date[] }>;
  const datesInRangeClassName = `date--in${value ? '' : '-selecting'}-range-disabled`;

  if (minDateRangeOverflow && minDate && startDate && isBefore(minDateRangeOverflow, minDate)) {
    highlightDates.push({ [datesInRangeClassName]: eachDayOfInterval({ start: minDateRangeOverflow, end: minDate }) });
  }

  if (maxDateRangeOverflow && maxDate && endDate && isAfter(maxDateRangeOverflow, maxDate)) {
    highlightDates.push({ [datesInRangeClassName]: eachDayOfInterval({ start: maxDate, end: maxDateRangeOverflow }) });
  }

  const excludeDates =
    selectableWeekDays?.length &&
    isAfter(maxDate, minDate) &&
    minDate !== minDateDefaultValue &&
    maxDateRounded !== maxDateDefaultValue
      ? eachDayOfInterval({ start: minDate, end: maxDate }).filter((day) => !selectableWeekDays.includes(day.getDay()))
      : [];

  if (excludeDates.length) {
    highlightDates.push({ 'react-datepicker__day--out-of-selection': excludeDates });
  }

  if (daysWithTooltip.length) {
    highlightDates.push({ 'react-datepicker__day--day-with-tooltip': daysWithTooltip });
  }

  const datePickerProps: ReactDatePickerProps = {
    endDate,
    maxDate,
    minDate,
    selectsEnd,
    startDate,
    calendarClassName: 'date-picker__calendar-wrapper',
    customInputRef: inputRef,
    fixedHeight: true,
    formatWeekDay: (formattedDate: string) => formattedDate.substring(0, 3),
    highlightDates: highlightDates.length ? highlightDates : undefined,
    onChange: onChangeHandler,
    popperClassName: classnames('date-picker__popper-wrapper', { weekpicker: isWeekPicker }),
    popperModifiers: {
      preventOverflow: {
        enabled: true,
        escapeWithReference: false,
        boundariesElement: 'viewport',
      },
    },
    renderDayContents: (dayOfMonth, date) => (
      <CustomDayElement
        dayOfMonth={dayOfMonth}
        date={date!}
        daysWithTooltip={daysWithTooltip}
        tooltipMessage={tooltipMessage}
      />
    ),
    selected: value,
    strictParsing: true,
    popperProps: {
      dataTestId: 'datepicker-calendar-wrapper',
      testId: 'datepicker-calendar-wrapper',
      'data-test-id': 'datepicker-calendar-wrapper',
    },
    popperContainer(props) {
      return (
        <div aria-labelledby={inputId} role="region" {...props}>
          {props.children}
        </div>
      );
    },
  };

  const invalid =
    (meta.initial && meta.invalid && !noValidation) || ((meta.touched || meta.dirty) && meta.invalid && !noValidation);
  const valid = (!meta.pristine && meta.valid && value && !noValidation) || (value && !meta.dirty && !noValidation);

  const customClassName = classnames({
    'date-picker-input--invalid': invalid && !disabled,
    'date-picker-input--valid': !invalid && valid && !disabled,
    'date-picker-input--disabled': disabled,
  });

  let RenderCustomInput: JSX.Element;
  const isWeekOrMonthPicker = isWeekPicker;

  if (isWeekOrMonthPicker) {
    const CustomPeriodPickerFieldInput = isWeekPicker && WeekPickerTextFieldInput;
    RenderCustomInput = (
      <CustomPeriodPickerFieldInput
        customClassName={customClassName}
        dataTestId={dataTestId}
        dateFormat={dateFormat}
        datePickerRef={datePickerRef}
        inputId={inputId}
        inputRef={inputRef}
        label={label}
        customOnChange={onChangeHandler}
      />
    );
  } else {
    RenderCustomInput = (
      <DatePickerTextFieldInput
        // Force rerender  when `value` used as initial value for useState() changes
        key={typeof value === 'number' || typeof value === 'string' ? value : value?.getTime()}
        customClassName={customClassName}
        dataTestId={dataTestId}
        dateFormat={dateFormat}
        datePickerRef={datePickerRef}
        inputId={inputId}
        inputRef={inputRef}
        label={label}
        maxDate={maxDate}
        minDate={minDate}
        onChange={onChangeHandler}
        customOnChange={onChangeHandler}
      />
    );
  }

  const renderCustomHeader = !isWeekOrMonthPicker
    ? (params: CustomHeaderParams) => <DatePickerHeader {...params} minDate={minDate} maxDate={maxDate} />
    : undefined;

  return (
    <DatePickerComponent
      {...datePickerProps}
      customInput={RenderCustomInput}
      ref={datePickerRef}
      {...rest}
      {...restInput}
      disabled={disabled}
      showYearDropdown={!isWeekOrMonthPicker}
      showMonthDropdown={!isWeekOrMonthPicker}
      renderCustomHeader={renderCustomHeader}
      locale={localeObj}
    />
  );
};

export default DatePicker;
